import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { HttpParams } from '@angular/common/http';
import { ProjectsService } from 'src/app/services/api/projects.service';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { NgbModal, NgbModalModule, NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { SavedDataInfo } from 'src/app/models/form/saved-data-info';
import { CategoriesService } from 'src/app/services/api/general-settings/categories.service';
import { InputFieldComponent } from 'src/app/components/form/input-field/input-field.component';
import { TextAreaFieldComponent } from 'src/app/components/form/textarea-field/textarea-field.component';
import { AlertInfo } from 'src/app/models/alert-info';
import { finalize } from 'rxjs/operators';
import { FormService } from 'src/app/services/form.service';
import { AuditLog } from 'src/app/models/audit-log.model';
import { RegulationsChangesLogComponent } from 'src/app/modules/general-settings/regulations/components/regulations-logs-change/regulations-changes-logs.component';

const params = new HttpParams().set('size', '9999');

@Component({
  selector: 'settings-regulation-to-new-testlist',
  templateUrl: './settings-regulation-to-new-testlist.component.html',
  styleUrls: ['./settings-regulation-to-new-testlist.component.scss'],
  imports: [CommonModule, NgbModalModule, FormsModule, ReactiveFormsModule, InputFieldComponent, TextAreaFieldComponent, NgbAlert, RegulationsChangesLogComponent],
})
export class SettingsRegulationToNewTestlistComponent implements OnInit, OnChanges, OnDestroy {
  private regulationsSubscription?: Subscription;
  @Input() jurisdictionId!: number;
  @Input() lawId!: number;
  @Input() checklistId!: number;
  @Input() testlist?: any;
  @Input() formFieldsComplete = false; 
  @Output() onSavedData = new EventEmitter<SavedDataInfo>();

  fromRegulation: any[] = [];
  toNewTestlist: any[] = [];
  toNewTestlistTemp: any[] = [];
  detailRegulation: any = null;
  isSaving = false;
  categoryOptions: { key: number; value: string }[] = [];
  selectedCategoryId: number | null = null;
  auditLogs: AuditLog[] = [];
  private destroy$ = new Subject<void>();
  private lastLoadedTestlistId: number | null = null;
  private isTestlistRegulationsLoading = false;
  private lastLoadedAuditLogTestlistId: number | null = null;
  private isAuditLogsLoading = false;
  private isRegulationsLoading = false;
  private lastJurisdictionId: number | null = null;
  private lastLawId: number | null = null;
  private lastRegulationsRequestKey: string | null = null;

  alertInfo: AlertInfo = { show: false, type: '', message: '' };

  testName: string = '';
  estimatedTimeHours: number = 0;
  estimatedTimeMinutes: number = 0;


  withoutEvidenceSteps: any[] = [{ id: 1, content: '' }];
  withEvidenceSteps: any[] = [{ id: 1, content: '' }];

  withoutEvidenceFormControls: FormControl[] = [];
  withEvidenceFormControls: FormControl[] = [];

  form: FormGroup = new FormGroup({
    name: new FormControl('', [Validators.required]),
    estimatedTimeHours: new FormControl(0, [Validators.min(0)]),
    estimatedTimeMinutes: new FormControl(0, [Validators.min(0), Validators.max(59)])
  });

  constructor(
    private projectsService: ProjectsService,
    private alertNotificationService: AlertNotificationService,
    private modalService: NgbModal,
    private categoriesService: CategoriesService,
    private formService: FormService
  ) { }

  initializeStepFormControls(): void {
    this.withoutEvidenceFormControls = this.withoutEvidenceSteps.map(step =>
      new FormControl(step.content)
    );

    this.withEvidenceFormControls = this.withEvidenceSteps.map(step =>
      new FormControl(step.content)
    );
  }

  ngOnInit(): void {
    this.categoriesService.list({ params }).subscribe((response: any) => {
      this.categoryOptions = response.data.categories.map((category: any) => ({ key: category.categoryId, value: category.name }));
    });
    this.initializeStepFormControls();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['testlist'] && changes['testlist'].currentValue && changes['testlist'].currentValue.testlistId) {
      const testlistId = changes['testlist'].currentValue.testlistId;
      this.loadTestlistRegulations();
      this.loadAuditLogs(testlistId);
    }

    const regulationPropsChanged = changes['checklistId'] || changes['jurisdictionId'] || changes['lawId'];
    const formFieldsCompleteChanged = changes['formFieldsComplete'];

    if ((regulationPropsChanged || formFieldsCompleteChanged) && this.formFieldsComplete && this.checklistId && this.jurisdictionId) {
      this.loadRegulations();
    }

    if (changes['testlist'] && changes['testlist'].currentValue) {
      const totalMinutes = this.testlist.estimatedTime || 0;
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      this.form.patchValue({
        name: this.testlist.name || '',
        estimatedTimeHours: hours,
        estimatedTimeMinutes: minutes
      });

      if (this.testlist.testlistStepList && Array.isArray(this.testlist.testlistStepList)) {
        this.loadTestSteps(this.testlist.testlistStepList);
      }
    }
  }

  filterByCategory(categoryId: number | null): void {
    this.selectedCategoryId = categoryId;

    if (this.formFieldsComplete && this.jurisdictionId && this.checklistId) {
      this.loadRegulations();
    } else {
      console.warn('Cannot filter by category - form fields not complete or missing required data');
    }
  }

  ngOnDestroy(): void {
    if (this.regulationsSubscription) {
      this.regulationsSubscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadAuditLogs(testlistId: number, force = false): void {
    if (!testlistId) {
      this.auditLogs = [];
      return;
    }

    if (!force && (this.isAuditLogsLoading || this.lastLoadedAuditLogTestlistId === testlistId)) {
      return;
    }

    this.isAuditLogsLoading = true;
    this.lastLoadedAuditLogTestlistId = testlistId;

    this.projectsService.listSettingsRegulationToTestlistChangelog(testlistId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isAuditLogsLoading = false;
        })
      )
      .subscribe({
        next: (response: any) => {
          this.auditLogs = response.data?.auditLogs || [];
        },
        error: (error: any) => {
          console.error('Error loading changelog:', error);
          this.auditLogs = [];
        }
      });
  }

  private lastChecklistId: number | null = null;
  private lastCategoryId: number | null = null;

  loadTestlistRegulations(force = false): void {
    if (!this.testlist?.testlistId) {
      console.warn('No testlist with valid ID provided, cannot load testlist regulations');
      return;
    }

    const testlistId = this.testlist.testlistId;
    if (!force && (this.isTestlistRegulationsLoading || this.lastLoadedTestlistId === testlistId)) {
      return;
    }

    this.isTestlistRegulationsLoading = true;
    this.lastLoadedTestlistId = testlistId;

    this.loadTestStepsFromAPI(testlistId);

    this.projectsService.listSettingsRegulationToTestlist(testlistId)
      .pipe(
        finalize(() => {
          this.isTestlistRegulationsLoading = false;
          if (this.checklistId && this.jurisdictionId) {
            this.loadRegulations();
          }
        })
      )
      .subscribe({
        next: (response: any) => {

          if (response && response.data && response.data.regulations) {

            this.toNewTestlistTemp = response.data.regulations;

            if (response.data.testlist) {
              const testlist = response.data.testlist;
              const totalMinutes = testlist.estimatedTime || 0;
              const hours = Math.floor(totalMinutes / 60);
              const minutes = totalMinutes % 60;

              this.form.patchValue({
                name: testlist.name || '',
                estimatedTimeHours: hours,
                estimatedTimeMinutes: minutes
              });

              if (testlist.testlistStepList && Array.isArray(testlist.testlistStepList)) {
                this.loadTestSteps(testlist.testlistStepList);
              }
            }
          } else {
            console.warn('No regulations found in testlist or unexpected response format:', response);
          }
        },
        error: (error) => {
          console.error('Error loading testlist regulations:', error);
          this.showAlert('danger', 'Error loading testlist regulations. Please try again.');
        }
      });
  }

  
  loadTestStepsFromAPI(testlistId: number): void {
    if (!testlistId) {
      console.warn('No testlist ID provided, cannot load test steps');
      return;
    }

    this.projectsService.listSettingsRegulationToTestlistSteps(testlistId)
      .subscribe({
        next: (response: any) => {
          if (response && response.data && response.data.testlistSteps) {
            const testlistSteps = response.data.testlistSteps;
            this.processTestSteps(testlistSteps);
          } else {
            console.warn('No test steps found in response or unexpected format:', response);
          }
        },
        error: (error) => {
          console.error('Error loading test steps:', error);
          this.showAlert('danger', 'Error loading test steps. Please try again.');
        }
      });
  }

  processTestSteps(testlistSteps: any[]): void {
    const withoutEvidenceSteps = testlistSteps
      .filter(step => step.type === 'WITHOUT_EVIDENCE')
      .map((step, index) => ({ 
        id: index + 1, 
        content: step.text,
        position: step.position,
        testlistStepId: step.testlistStepId || undefined 
      }))
      .sort((a, b) => a.position - b.position);

    if (withoutEvidenceSteps.length > 0) {
      this.withoutEvidenceSteps = withoutEvidenceSteps;
      this.withoutEvidenceFormControls = this.withoutEvidenceSteps.map(step =>
        new FormControl(step.content, [Validators.required])
      );
    } else {
      this.withoutEvidenceSteps = [{ id: 1, content: '' }];
      this.withoutEvidenceFormControls = [new FormControl('', [Validators.required])];
    }

    const withEvidenceSteps = testlistSteps
      .filter(step => step.type === 'WITH_EVIDENCE')
      .map((step, index) => ({ 
        id: index + 1, 
        content: step.text,
        position: step.position,
        testlistStepId: step.testlistStepId || undefined 
      }))
      .sort((a, b) => a.position - b.position);

    if (withEvidenceSteps.length > 0) {
      this.withEvidenceSteps = withEvidenceSteps;
      this.withEvidenceFormControls = this.withEvidenceSteps.map(step =>
        new FormControl(step.content, [Validators.required])
      );
    } else {
      this.withEvidenceSteps = [{ id: 1, content: '' }];
      this.withEvidenceFormControls = [new FormControl('', [Validators.required])];
    }
  }

  loadTestSteps(testlistStepList: any[]): void {
    const withoutEvidenceSteps = testlistStepList
      .filter(step => step.type === 'without_evidence')
      .map((step, index) => ({ id: index + 1, content: step.text }));

    if (withoutEvidenceSteps.length > 0) {
      this.withoutEvidenceSteps = withoutEvidenceSteps;
      this.withoutEvidenceFormControls = this.withoutEvidenceSteps.map(step =>
        new FormControl(step.content, [Validators.required])
      );
    }

    const withEvidenceSteps = testlistStepList
      .filter(step => step.type === 'with_evidence')
      .map((step, index) => ({ id: index + 1, content: step.text }));

    if (withEvidenceSteps.length > 0) {
      this.withEvidenceSteps = withEvidenceSteps;
      this.withEvidenceFormControls = this.withEvidenceSteps.map(step =>
        new FormControl(step.content, [Validators.required])
      );
    }
  }

  loadRegulations(): void {
    if (!this.formFieldsComplete) {
      this.fromRegulation = [];
      return;
    }

    const checklistChanged = this.lastChecklistId !== this.checklistId;
    const categoryChanged = this.lastCategoryId !== this.selectedCategoryId;
    const jurisdictionChanged = this.lastJurisdictionId !== this.jurisdictionId;
    const lawChanged = this.lastLawId !== this.lawId;
    const shouldReload = checklistChanged || categoryChanged || jurisdictionChanged || lawChanged || this.fromRegulation.length === 0;

    if (this.jurisdictionId && this.checklistId && shouldReload) {
      const requestKey = `${this.checklistId}-${this.selectedCategoryId ?? 'all'}`;
      if (this.isRegulationsLoading && this.lastRegulationsRequestKey === requestKey) {
        return;
      }

      this.isRegulationsLoading = true;
      this.lastRegulationsRequestKey = requestKey;
      this.lastChecklistId = this.checklistId;
      this.lastCategoryId = this.selectedCategoryId;
      this.lastJurisdictionId = this.jurisdictionId;
      this.lastLawId = this.lawId;

      let queryParams = new HttpParams()
        .set('size', '9999')
        .set('checklistId', this.checklistId.toString());

      if (this.selectedCategoryId) {
        queryParams = queryParams.set('categoryId', this.selectedCategoryId.toString());
      }

      if (this.regulationsSubscription) {
        this.regulationsSubscription.unsubscribe();
      }

      this.regulationsSubscription = this.projectsService.regulations$.subscribe({
        next: (regulations: any[]) => {
          this.fromRegulation = regulations;
          this.isRegulationsLoading = false;

          this.updateSelectedRegulationsState();
        },
        error: (error) => {
          console.error('Error subscribing to regulations:', error);
          this.fromRegulation = [];
          this.isRegulationsLoading = false;
          this.showAlert('danger', 'Error loading regulations. Please try again.');
        }
      });

      this.projectsService.getRegulationList(queryParams);
    } else if (!this.jurisdictionId || !this.checklistId) {
      console.warn('JurisdictionId, lawId, and checklistId must all be set to load regulations.');
      this.fromRegulation = [];
    } else {
      console.log('No need to reload regulations - no relevant changes detected');
    }
  }

  updateSelectedRegulationsState(): void {
    if (!this.fromRegulation || !this.toNewTestlistTemp) {
      return;
    }

    const selectedRegulationIds = this.toNewTestlistTemp.map(r => r.regulationId);
    this.fromRegulation.forEach(reg => {
      const regId = reg.regulationId;
      const isSelected = this.isTemporarilySelected(reg);
    });

    if (selectedRegulationIds.length > 0) {
      console.log(`${selectedRegulationIds.length} regulations are currently selected`);
    }
  }

  isTemporarilySelected(regulation: any): boolean {
    const fromRegulationId = regulation.regulationId;

    if (!fromRegulationId || !this.toNewTestlistTemp || this.toNewTestlistTemp.length === 0) {
      return false;
    }

    return this.toNewTestlistTemp.some(r => {
      const toRegulationId = r.regulationId;
      return toRegulationId === fromRegulationId;
    });
  }

  toggleSelection(regulation: any): void {
    const regulationId = regulation.regulationId;

    if (this.isTemporarilySelected(regulation)) {
      this.toNewTestlistTemp = this.toNewTestlistTemp.filter(r => {
        const toRegulationId = r.regulationId;
        return toRegulationId !== regulationId;
      });
    } else {
      this.toNewTestlistTemp.push(regulation);
    }

    this.updateSelectedRegulationsState();
  }

  addStep(withEvidence: boolean): void {
    const stepsArray = withEvidence ? this.withEvidenceSteps : this.withoutEvidenceSteps;
    const controlsArray = withEvidence ? this.withEvidenceFormControls : this.withoutEvidenceFormControls;

    const newId = stepsArray.length > 0 ? Math.max(...stepsArray.map(step => step.id)) + 1 : 1;
    stepsArray.push({ 
      id: newId, 
      content: '',
      position: stepsArray.length + 1 
    });

    controlsArray.push(new FormControl('', [Validators.required]));
  }

  removeStep(index: number, withEvidence: boolean): void {
    const stepsArray = withEvidence ? this.withEvidenceSteps : this.withoutEvidenceSteps;
    const controlsArray = withEvidence ? this.withEvidenceFormControls : this.withoutEvidenceFormControls;

    if (stepsArray.length > 1) {
      stepsArray.splice(index, 1);
      controlsArray.splice(index, 1);
    } else {
      this.showAlert('warning', 'At least one step is required');
    }
  }

  updateStepContent(index: number, withEvidence: boolean): void {
    const stepsArray = withEvidence ? this.withEvidenceSteps : this.withoutEvidenceSteps;
    const controlsArray = withEvidence ? this.withEvidenceFormControls : this.withoutEvidenceFormControls;

    stepsArray[index].content = controlsArray[index].value;
  }

  openDetails(regulation: any, content: any): void {
    this.detailRegulation = regulation;
    this.modalService.open(content, { size: 'lg' });
  }

  private validateAllFormControls(): boolean {
    this.form.markAllAsTouched();

    for (const control of this.withoutEvidenceFormControls) {
      control.markAsTouched();
      if (control.invalid) {
        return false;
      }
    }

    for (const control of this.withEvidenceFormControls) {
      control.markAsTouched();
      if (control.invalid) {
        return false;
      }
    }

    return this.form.valid;
  }



  /**
    * *******************
    * Submit form methods
    * *******************
    */
  showAlertInfo(type: string, message: string) {
    this.alertInfo = {
      show: true,
      type,
      message
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      this.alertInfo.show = false;
    }, 3000);
  }
  updateValidationErrors(validationErrors: any) {
    this.formService.resetErrors(this.form);
    this.formService.setValidationErrors(this.form, validationErrors);
  }
  submitFormSuccess(response: any) {
    this.alertNotificationService.success('Operation completed successfully')
    this.showAlertInfo('success', 'Operation completed successfully');

    this.onSavedData.emit({
      success: true,
      response
    });
  }
  submitFormError(error: any) {

    if (error.error.error.description == 'ARGUMENT_VALIDATION_ERROR') {
      this.updateValidationErrors(error.error.error.validationErrors);
    }
    else if (error.error.error.description == 'USER_EMAIL_ALREADY_EXISTS') {
      this.updateValidationErrors([{ field: 'email', defaultMessage: error.error.error.message }]);
      this.showAlertInfo('danger', `Please fix the error: ${error.error.error.message}`);
    }
    else {
      this.showAlertInfo('danger', error.error.error.message);
    }

    this.onSavedData.emit({
      success: false,
      error
    });

  } submitForm(): void {
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      control?.markAsTouched();
      control?.updateValueAndValidity();
    });

    if (this.form.valid) {
      const values = Object.assign(
        this.form.value,
        {
          estimatedTime: (Number(this.form.value.estimatedTimeHours) * 60) + Number(this.form.value.estimatedTimeMinutes),
          tenantId: 1,
        }
      )
      delete values.estimatedTimeHours,
      delete values.estimatedTimeMinutes

      this.alertInfo.show = false;
      this.formService.resetErrors(this.form);

      const serviceMethod = this.testlist
        ? this.projectsService.updateSettingsTestlist(this.testlist.testlistId, values)
        : this.projectsService.createSettingsTestlist(values);

      serviceMethod.subscribe({
        next: (response: any) => {
          if (this.testlist) {
            this.saveTestlistRegulations(response);
          } else {
            this.testlist = response.data.testlist;
            this.saveTestlistRegulations(response);
          }
        },
        error: (error: any) => {
          this.submitFormError(error);
          this.isSaving = false;
        },
      });
    }
  }

  saveTestlistRegulations(previousResponse: any): void {
    const testlistJoinRegulations = this.toNewTestlistTemp.map(regulationTest => ({
      regulationId: regulationTest.regulationId,
      checklistId: this.checklistId
    }));

    const testlistData = {
      testlistJoinRegulations: testlistJoinRegulations
    };

    const testlistId = this.testlist.testlistId;
    
    if (!testlistId) {
      this.showAlert('danger', 'No testlist ID found for saving regulations');
      this.isSaving = false;
      return;
    }

    const saveRegulationsOperation = this.projectsService.updateSettingsRegulationToTestlist(testlistId, testlistData);

    saveRegulationsOperation.subscribe({
      next: (response) => {
        this.saveTestlistSteps(testlistId);
      },
      error: (error) => {
        this.showAlert('danger', 'Failed to save testlist regulations. Please try again.');
        this.isSaving = false;

        this.onSavedData.emit({
          success: false,
          error: error
        });
      }
    });
  }

  saveTestlistSteps(testlistId: number): void {
    const withEvidenceStepsData = this.withEvidenceSteps.map((step, index) => {
      const stepData: any = {
        type: 'WITH_EVIDENCE',
        text: step.content,
        position: index + 1
      };
      
      if (step.testlistStepId) {
        stepData.testlistStepId = step.testlistStepId;
      }
      
      return stepData;
    });

    const withoutEvidenceStepsData = this.withoutEvidenceSteps.map((step, index) => {
      const stepData: any = {
        type: 'WITHOUT_EVIDENCE',
        text: step.content,
        position: index + 1
      };
      
      if (step.testlistStepId) {
        stepData.testlistStepId = step.testlistStepId;
      }
      
      return stepData;
    });

    const stepsData = {
      testlistSteps: [...withEvidenceStepsData, ...withoutEvidenceStepsData]
    };

    this.projectsService.createSettingsRegulationToTestlistSteps(testlistId, stepsData).subscribe({
      next: (response) => {
        this.showAlert('success', 'Testlist saved successfully!');

        this.loadTestlistRegulations(true);
        this.loadAuditLogs(testlistId, true);
        
        this.onSavedData.emit({
          success: true,
          response: response
        });

        this.isSaving = false;
      },
      error: (error) => {
        this.showAlert('danger', 'Failed to save testlist steps. Please try again.');
        this.isSaving = false;

        this.onSavedData.emit({
          success: false,
          error: error
        });
      }
    });
  }
  
  saveTestlist(): void {
    if (!this.validateAllFormControls()) {
      this.showAlert('danger', 'Please fill in all required fields correctly!');
      return;
    }

    if (this.toNewTestlistTemp.length === 0) {
      this.showAlert('danger', 'Please select at least one regulation!');
      return;
    }

    this.isSaving = true;
    
    this.submitForm();
  }
  /**
   * *******************
   * Submit form methods
   * *******************
   */

  getTestNameInput(): FormControl {
    return this.form.get('name') as FormControl;
  }

  getEstimatedTimeHoursInput(): FormControl {
    return this.form.get('estimatedTimeHours') as FormControl;
  }

  getEstimatedTimeMinutesInput(): FormControl {
    return this.form.get('estimatedTimeMinutes') as FormControl;
  }

  getStepFormControls(withEvidence: boolean): FormControl[] {
    const stepsArray = withEvidence ? this.withEvidenceSteps : this.withoutEvidenceSteps;
    return stepsArray.map(() => new FormControl('', [Validators.required]));
  }

  private showAlert(type: string, message: string): void {
    this.alertInfo = { show: true, type, message };

    setTimeout(() => {
      this.alertInfo.show = false;
    }, 5000);

    switch (type) {
      case 'success':
        this.alertNotificationService.success(message);
        break;
      case 'warning':
        this.alertNotificationService.warning(message);
        break;
      case 'danger':
      case 'error':
        this.alertNotificationService.error(message);
        break;
      default:
        this.alertNotificationService.success(message);
    }
  }

  closeAlert(): void {
    this.alertInfo.show = false;
  }
}
